import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import VoicePreview from "@/components/VoicePreview";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Progress } from "@/components/ui/progress";
import { Play, CheckCircle2, Lock, Clock, BookOpen, Award, ChevronLeft, ChevronRight, Volume2, FileText, Headphones, Download } from "lucide-react";
import { toast } from "sonner";

function renderInline(text) {
  // Handle **bold** and *italic* inside any text block
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*)/g);
  return parts.map((p, j) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={j}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) return <em key={j}>{p.slice(1, -1)}</em>;
    return p;
  });
}

function renderLessonBody(md = "") {
  const blocks = md.split(/\n{2,}/);
  return blocks.map((b, i) => {
    if (b.startsWith("### ")) return <h3 key={i} className="font-display text-xl mt-6 mb-3 text-neutral-900">{renderInline(b.slice(4))}</h3>;
    if (b.startsWith("## ")) return <h2 key={i} className="font-display text-2xl mt-8 mb-3 text-neutral-900">{renderInline(b.slice(3))}</h2>;
    if (b.startsWith("# ")) return <h2 key={i} className="font-display text-3xl mt-8 mb-4 text-neutral-900">{renderInline(b.slice(2))}</h2>;
    if (b.startsWith("> ")) return <blockquote key={i} className="border-l-4 border-brand-pink pl-5 italic text-neutral-700 my-5">{renderInline(b.slice(2))}</blockquote>;
    if (/^\d+\.\s/.test(b) || b.startsWith("- ")) {
      const items = b.split("\n").map((li) => li.replace(/^(\d+\.\s|-\s)/, ""));
      const Tag = /^\d+\./.test(b) ? "ol" : "ul";
      return <Tag key={i} className={`${Tag === "ol" ? "list-decimal" : "list-disc"} pl-6 space-y-2 my-4 text-neutral-800`}>{items.map((li, j) => <li key={j} className="leading-relaxed">{renderInline(li)}</li>)}</Tag>;
    }
    return <p key={i} className="text-neutral-800 leading-relaxed my-4">{renderInline(b)}</p>;
  });
}

function LessonReader({ courseId, lesson, moduleId, onFinished, canDownload }) {
  const hasAudio = !!lesson.audio_url;
  const hasBody = !!(lesson.body_md && lesson.body_md.trim());
  const base = process.env.REACT_APP_BACKEND_URL;
  const token = typeof window !== "undefined" ? localStorage.getItem("hh_token") : null;

  const downloadWithAuth = async (kind) => {
    try {
      const res = await fetch(
        `${base}/api/courses/${courseId}/lessons/${moduleId}/${lesson.id}/${kind}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Download failed (${res.status})`);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = res.headers.get("content-disposition") || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      a.download = match ? match[1] : `host-hustle-${lesson.id}.${kind === "pdf" ? "pdf" : "mp3"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(kind === "pdf" ? "PDF downloaded" : "Audio downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="rounded-[2rem] bg-white border border-neutral-100 overflow-hidden" data-testid="lesson-reader">
      {hasAudio && (
        <div className="bg-neutral-950 text-white p-6">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full bg-brand-pink/25 flex items-center justify-center flex-shrink-0">
              <Headphones className="w-5 h-5 text-brand-pink" />
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.22em] uppercase text-brand-pink mb-1">Listen · Narrated by Kristy</div>
              <audio data-testid="lesson-audio" controls src={lesson.audio_url} onEnded={onFinished} className="w-full mt-1" />
            </div>
          </div>
          {canDownload && (
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={() => downloadWithAuth("audio")}
                data-testid={`download-audio-${lesson.id}`}
                className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand-pink hover:text-white transition"
              >
                <Download className="w-3 h-3" /> Download MP3
              </button>
            </div>
          )}
        </div>
      )}
      <div className="p-8 lg:p-10">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-brand-pink">
            <FileText className="w-3.5 h-3.5" /> Written Lesson · {lesson.read_minutes || 5} min read
          </div>
          {canDownload && hasBody && (
            <button
              type="button"
              onClick={() => downloadWithAuth("pdf")}
              data-testid={`download-pdf-${lesson.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-brand-pink hover:text-neutral-900 transition"
            >
              <Download className="w-3 h-3" /> Download PDF
            </button>
          )}
        </div>
        {hasBody ? (
          <article className="max-w-none">{renderLessonBody(lesson.body_md)}</article>
        ) : (
          <div className="text-neutral-500 leading-relaxed">
            {lesson.summary || "Kristy is finishing this lesson's written notes — they'll appear here shortly. Until then, use the summary + action step below to make progress today."}
          </div>
        )}
      </div>
      {hasBody && (
        <div className="px-8 lg:px-10 pb-8 pt-2 text-xs text-neutral-400 italic text-center">
          Written & narrated by Kristy Russo · Host Hustle™
        </div>
      )}
    </div>
  );
}


export default function CourseDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [prog, setProg] = useState({ enrolled: false, pct: 0, done: 0, total: 0, completed_ids: [] });
  const [activeIdx, setActiveIdx] = useState({ m: 0, l: 0 });
  const readerRef = useRef(null);
  const firstMount = useRef(true);

  const loadProgress = async () => {
    if (!user) return;
    try {
      const { data } = await api.get(`/me/courses/${id}/progress`);
      setProg(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    api.get(`/courses/${id}`).then((r) => setCourse(r.data));
    loadProgress();
    // eslint-disable-next-line
  }, [id, user]);

  const flat = useMemo(() => {
    if (!course) return [];
    const arr = [];
    course.modules.forEach((m, mi) => m.lessons.forEach((l, li) => arr.push({ m, l, mi, li })));
    return arr;
  }, [course]);

  const active = flat[flat.findIndex((x) => x.mi === activeIdx.m && x.li === activeIdx.l)] || flat[0];

  const enroll = async () => {
    if (!user) return nav("/login");
    try {
      if (course.price && course.price > 0) {
        const { data } = await api.post("/payments/checkout", {
          item_type: "course",
          item_id: course.id,
          origin_url: window.location.origin,
        });
        window.location.href = data.checkout_url;
        return;
      }
      await api.post("/courses/enroll", { course_id: id });
      toast.success("You're enrolled! Let's build.");
      await loadProgress();
    } catch { toast.error("Could not enroll."); }
  };

  const markComplete = async (moduleId, lessonId) => {
    if (!user) return nav("/login");
    if (!prog.enrolled) return toast.info("Enroll first to track progress.");
    try {
      await api.post("/courses/progress", { course_id: id, lesson_id: `${moduleId}-${lessonId}`, completed: true });
      await loadProgress();
      toast.success("Marked complete");
    } catch {}
  };

  const nextLesson = () => {
    if (!course) return;
    const cur = flat.findIndex((x) => x.mi === activeIdx.m && x.li === activeIdx.l);
    const next = flat[cur + 1];
    if (next) setActiveIdx({ m: next.mi, l: next.li });
  };
  const prevLesson = () => {
    const cur = flat.findIndex((x) => x.mi === activeIdx.m && x.li === activeIdx.l);
    const prev = flat[cur - 1];
    if (prev) setActiveIdx({ m: prev.mi, l: prev.li });
  };

  const isAdmin = user?.role === "admin";
  const canSeeLessons = prog.enrolled || isAdmin;

  // Smooth-scroll the lesson reader into view whenever the user picks a new lesson
  // (skipping the initial render so the page doesn't jump on first load)
  useEffect(() => {
    if (firstMount.current) { firstMount.current = false; return; }
    if (readerRef.current) {
      readerRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeIdx.m, activeIdx.l]);

  if (!course) return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <div className="max-w-6xl mx-auto p-24 text-center text-neutral-500">Loading course…</div>
    </div>
  );

  return (
    <div className="bg-white min-h-screen">
      <Navbar />
      <section className="bg-neutral-950 text-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">{course.category}</div>
            <h1 className="font-display text-4xl md:text-5xl leading-tight">{course.title}</h1>
            <p className="mt-4 text-white/70 text-lg">{course.tagline}</p>
            <div className="flex flex-wrap gap-6 mt-8 text-sm text-white/70">
              <span className="inline-flex items-center gap-2"><BookOpen className="w-4 h-4" /> {course.chapters_count} chapters</span>
              <span className="inline-flex items-center gap-2"><Clock className="w-4 h-4" /> {course.duration_hrs} hours</span>
              <span className="inline-flex items-center gap-2"><Award className="w-4 h-4" /> Certificate</span>
            </div>
            {prog.enrolled ? (
              <div className="mt-8">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-white/70">Your progress</span>
                  <span className="text-brand-pink font-medium">{prog.pct}% · {prog.done}/{prog.total}</span>
                </div>
                <Progress value={prog.pct} className="h-2" />
                {prog.pct === 100 && (
                  <Button asChild className="mt-6 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white btn-pink-glow" data-testid="view-certificate-btn">
                    <Link to={`/certificates/${course.id}`}><Award className="w-4 h-4 mr-2" /> View your certificate</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="mt-8 flex flex-wrap gap-3 items-center">
                <Button onClick={enroll} data-testid="course-enroll-btn" size="lg" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white px-8 btn-pink-glow">
                  {course.price === 0 ? "Enroll Free" : `Enroll · $${course.price}`}
                </Button>
                {(() => {
                  for (const m of course.modules || []) {
                    for (const l of m.lessons || []) {
                      if (l.audio_url) {
                        return (
                          <VoicePreview
                            audioUrl={l.audio_url}
                            previewSeconds={30}
                            label="Hear Kristy narrate lesson 1"
                            variant="dark"
                            testId="course-hero-voice-preview"
                          />
                        );
                      }
                    }
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl overflow-hidden border border-white/10 aspect-[4/3] relative bg-black">
              <img src={course.cover_image} alt={course.title} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-white/90 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <BookOpen className="w-3 h-3" /> {course.chapters_count} chapters · {course.duration_hrs}h
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10">
        {/* Player */}
        <div className="lg:col-span-8 order-2 lg:order-1" ref={readerRef}>
          <div className="relative scroll-mt-24">
            {prog.enrolled && active ? (
              <LessonReader
                courseId={course.id}
                moduleId={active.m.id}
                lesson={active.l}
                onFinished={() => markComplete(active.m.id, active.l.id)}
                canDownload={true}
              />
            ) : isAdmin && active ? (
              <div>
                <div className="mb-3 inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-brand-pink bg-brand-blush border border-brand-pink/20 rounded-full px-3 py-1" data-testid="admin-preview-badge">
                  <Award className="w-3 h-3" /> Admin preview · what students see
                </div>
                <LessonReader
                  courseId={course.id}
                  moduleId={active.m.id}
                  lesson={active.l}
                  onFinished={() => {}}
                  canDownload={true}
                />
              </div>
            ) : (
              <div className="w-full rounded-3xl bg-neutral-950 relative overflow-hidden flex items-center justify-center p-12">
                <img src={course.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
                <div className="relative text-center text-white p-8 max-w-md">
                  <Lock className="w-8 h-8 mx-auto text-brand-pink mb-3" />
                  <div className="font-display text-2xl mb-2">Enroll to unlock the lesson</div>
                  <div className="text-sm text-white/70 mb-4">Written lesson + audio narration + action step + downloadable PDF included.</div>
                  <Button onClick={enroll} className="mt-2 rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white" data-testid="locked-enroll-btn">
                    {course.price === 0 ? "Enroll Free" : `Enroll · $${course.price}`}
                  </Button>
                </div>
              </div>
            )}
          </div>
          {active && (
            <div className="mt-6 flex items-start justify-between gap-4">
              <div>
                <div className="text-xs uppercase tracking-widest text-brand-pink mb-1">{active.m.title}</div>
                <div className="font-display text-2xl">{active.l.title}</div>
                <div className="text-sm text-neutral-500 mt-1">{active.l.duration}</div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={prevLesson} data-testid="lesson-prev" className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" onClick={nextLesson} data-testid="lesson-next" className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
                {prog.enrolled && (
                  <Button onClick={() => markComplete(active.m.id, active.l.id)} data-testid="mark-complete-btn" className="rounded-full bg-brand-pink text-white hover:bg-brand-pink/90">
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Mark complete
                  </Button>
                )}
              </div>
            </div>
          )}

          {active && (active.l.summary || active.l.action_step) && (
            <div className="mt-6 grid md:grid-cols-2 gap-4">
              {active.l.summary && (
                <div className="rounded-2xl bg-neutral-50 border border-neutral-100 p-5" data-testid="lesson-summary">
                  <div className="text-xs uppercase tracking-widest text-neutral-500 mb-2">Lesson summary</div>
                  <p className="text-sm text-neutral-800 leading-relaxed">{active.l.summary}</p>
                </div>
              )}
              {active.l.action_step && (
                <div className="rounded-2xl bg-brand-blush/60 border border-brand-pink/20 p-5" data-testid="lesson-action-step">
                  <div className="text-xs uppercase tracking-widest text-brand-pink mb-2 flex items-center gap-2">
                    <span className="inline-block w-2 h-2 rounded-full bg-brand-pink" />
                    Host Hustle Action Step
                  </div>
                  <p className="text-sm text-neutral-900 leading-relaxed font-medium">{active.l.action_step}</p>
                </div>
              )}
            </div>
          )}

          {canSeeLessons && (
            <div className="mt-6">
              <button
                type="button"
                data-testid="download-workbook-btn"
                onClick={async () => {
                  const base = process.env.REACT_APP_BACKEND_URL;
                  const token = localStorage.getItem("hh_token");
                  try {
                    const res = await fetch(
                      `${base}/api/courses/${course.id}/workbook.pdf`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (!res.ok) { toast.error("Workbook download failed"); return; }
                    const blob = await res.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    const disp = res.headers.get("content-disposition") || "";
                    const match = disp.match(/filename="?([^";]+)"?/i);
                    a.download = match ? match[1] : `host-hustle-${course.id}-workbook.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(url);
                    toast.success("Workbook downloaded");
                  } catch { toast.error("Workbook download failed"); }
                }}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-neutral-900 border border-neutral-200 hover:border-brand-pink hover:text-brand-pink transition text-sm font-medium shadow-sm"
              >
                <Download className="w-4 h-4" /> Download the whole course as a workbook
              </button>
              <div className="text-xs text-neutral-500 mt-2 ml-1">
                One branded PDF · cover + table of contents + every lesson with action steps
              </div>
            </div>
          )}
        </div>

        {/* Curriculum */}
        <div className="lg:col-span-4 order-1 lg:order-2">
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Curriculum</div>
          <h2 className="font-display text-2xl mb-4">{course.modules.length} modules · {prog.total || flat.length} lessons</h2>
          <Accordion type="single" collapsible defaultValue={course.modules[0]?.id} className="space-y-2">
            {course.modules.map((m, mi) => (
              <AccordionItem key={m.id} value={m.id} className="rounded-2xl border border-neutral-200 px-4 bg-white" data-testid={`module-${m.id}`}>
                <AccordionTrigger className="hover:no-underline py-4">
                  <div className="flex items-center gap-3 text-left">
                    <span className="font-display text-xl text-brand-pink">{String(mi + 1).padStart(2, "0")}</span>
                    <div>
                      <div className="font-display text-base">{m.title}</div>
                      <div className="text-xs text-neutral-500">{m.lessons.length} lessons</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-4">
                  <ul className="space-y-1">
                    {m.lessons.map((l, li) => {
                      const done = (prog.completed_ids || []).includes(`${m.id}-${l.id}`);
                      const isActive = activeIdx.m === mi && activeIdx.l === li;
                      return (
                        <li key={l.id}>
                          <button
                            onClick={() => setActiveIdx({ m: mi, l: li })}
                            data-testid={`lesson-${m.id}-${l.id}`}
                            className={`w-full flex items-center gap-3 text-left p-2.5 rounded-xl transition ${isActive ? "bg-brand-blush" : "hover:bg-neutral-50"}`}
                          >
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${done ? "bg-brand-pink text-white" : "bg-brand-blush text-brand-pink"}`}>
                              {done ? <CheckCircle2 className="w-4 h-4" /> : <BookOpen className="w-3.5 h-3.5" />}
                            </span>
                            <span className="flex-1 min-w-0">
                              <div className="text-sm font-medium truncate">{l.title}</div>
                              <div className="text-xs text-neutral-500">{l.duration}</div>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
      <Footer />
    </div>
  );
}
